import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import { PortalProvider } from "@/lib/PortalContext";
import { Toaster } from "sonner";

// Public
import LandingPage from "@/pages/LandingPage";
import DesignsPage from "@/pages/public/DesignsPage";
import DesignDetailPage from "@/pages/public/DesignDetailPage";
import PublicMaterialsPage from "@/pages/public/PublicMaterialsPage";
import ProcessPage from "@/pages/public/ProcessPage";
import InvestmentPage from "@/pages/public/InvestmentPage";
import ArchitectsPage from "@/pages/public/ArchitectsPage";
import AboutPage from "@/pages/public/AboutPage";
import EnquiryPage from "@/pages/public/EnquiryPage";
import ApplyPage from "@/pages/apply/ApplyPage";

// Auth
import LoginPage from "@/pages/LoginPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import AuthCallback from "@/pages/AuthCallback";

// Studio
import DashboardPage from "@/pages/DashboardPage";
import ClientsPage from "@/pages/ClientsPage";
import ProductsPage from "@/pages/ProductsPage";
import MaterialsImportPage from "@/pages/MaterialsImportPage";
import MaterialsThumbnailsPage from "@/pages/MaterialsThumbnailsPage";
import SupplierAssetLibrariesPage from "@/pages/SupplierAssetLibrariesPage";
import ProjectsPage from "@/pages/ProjectsPage";
import ProjectBuilderPage from "@/pages/ProjectBuilderPage";
import ProposalPreviewPage from "@/pages/ProposalPreviewPage";
import SettingsPage from "@/pages/SettingsPage";
import LeadsPage from "@/pages/LeadsPage";
import LeadDetailPage from "@/pages/LeadDetailPage";
import SuppliersPage from "@/pages/SuppliersPage";
import PricingPage from "@/pages/PricingPage";
import AdminBetaApplicationsPage from "@/pages/AdminBetaApplicationsPage";
import AppLayout from "@/components/AppLayout";
import OnboardingModal from "@/components/OnboardingModal";

// Portal
import PortalRedeem from "@/pages/portal/PortalRedeem";
import PortalHomePage from "@/pages/portal/PortalHomePage";
import PortalProjectPage from "@/pages/portal/PortalProjectPage";
import PortalConceptsPage from "@/pages/portal/PortalConceptsPage";
import PortalLogin from "@/pages/portal/PortalLogin";
import PortalForgotPassword from "@/pages/portal/PortalForgotPassword";
import PortalResetPassword from "@/pages/portal/PortalResetPassword";
import PortalAccountPage from "@/pages/portal/PortalAccountPage";
import PortalAccess from "@/pages/portal/PortalAccess";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-[11px] tracking-[0.32em] font-bold" style={{ color: "#A68A64", lineHeight: 1.05 }}>SUBTEND</div>
          <div className="text-[11px] tracking-[0.32em] font-bold text-foreground mt-1" style={{ lineHeight: 1.05 }}>STUDIO</div>
          <div className="font-serif text-2xl mt-5">Loading studio…</div>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace/>;
  const showOnb = user.onboarding_status === "not_started" || user.onboarding_status === "in_progress";
  return (
    <AppLayout>
      {showOnb && <OnboardingModal user={user} onFinish={() => { window.location.reload(); }}/>}
      {children}
    </AppLayout>
  );
}

function RootRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback/>;
  }
  return (
    <Routes>
      <Route path="/" element={<LandingPage/>}/>
      <Route path="/terms" element={<TermsPage/>}/>
      <Route path="/privacy" element={<PrivacyPage/>}/>
      <Route path="/designs" element={<DesignsPage/>}/>
      <Route path="/designs/:slug" element={<DesignDetailPage/>}/>
      <Route path="/materials" element={<PublicMaterialsPage/>}/>
      <Route path="/process" element={<ProcessPage/>}/>
      <Route path="/investment" element={<InvestmentPage/>}/>
      <Route path="/architects" element={<ArchitectsPage/>}/>
      <Route path="/about" element={<AboutPage/>}/>
      <Route path="/enquiry" element={<EnquiryPage/>}/>
      <Route path="/apply" element={<ApplyPage/>}/>

      <Route path="/login" element={<LoginPage mode="login"/>}/>
      <Route path="/register" element={<LoginPage mode="register"/>}/>

      <Route path="/dashboard" element={<Protected><DashboardPage/></Protected>}/>
      <Route path="/leads" element={<Protected><LeadsPage/></Protected>}/>
      <Route path="/leads/:id" element={<Protected><LeadDetailPage/></Protected>}/>
      <Route path="/clients" element={<Protected><ClientsPage/></Protected>}/>
      <Route path="/products" element={<Protected><ProductsPage/></Protected>}/>
      <Route path="/materials/import" element={<Protected><MaterialsImportPage/></Protected>}/>
      <Route path="/materials/thumbnails" element={<Protected><MaterialsThumbnailsPage/></Protected>}/>
      <Route path="/suppliers" element={<Protected><SuppliersPage/></Protected>}/>
      <Route path="/pricing" element={<Protected><PricingPage/></Protected>}/>
      <Route path="/projects" element={<Protected><ProjectsPage/></Protected>}/>
      <Route path="/projects/:id" element={<Protected><ProjectBuilderPage/></Protected>}/>
      <Route path="/projects/:id/proposal" element={<Protected><ProposalPreviewPage/></Protected>}/>
      <Route path="/settings" element={<Protected><SettingsPage/></Protected>}/>
      <Route path="/settings/supplier-libraries" element={<Protected><SupplierAssetLibrariesPage/></Protected>}/>
      {/* Admin-only route — intentionally hidden from Studio nav. Backend
          returns 404 for non-admin users so the URL cannot be probed. */}
      <Route path="/admin/beta-applications" element={<Protected><AdminBetaApplicationsPage/></Protected>}/>

      <Route path="/portal/redeem" element={<PortalRedeem/>}/>
      <Route path="/portal/access" element={<PortalAccess/>}/>
      <Route path="/portal/login" element={<PortalLogin/>}/>
      <Route path="/portal/forgot-password" element={<PortalForgotPassword/>}/>
      <Route path="/portal/reset-password" element={<PortalResetPassword/>}/>
      <Route path="/portal/account" element={<PortalAccountPage/>}/>
      <Route path="/portal" element={<PortalHomePage/>}/>
      <Route path="/portal/projects/:id" element={<PortalProjectPage/>}/>
      <Route path="/portal/projects/:id/concepts" element={<PortalConceptsPage/>}/>
      <Route path="/portal/projects/:id/concepts/:dvid" element={<PortalConceptsPage/>}/>

      <Route path="*" element={<Navigate to="/" replace/>}/>
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PortalProvider>
          <RootRouter/>
          <Toaster position="bottom-right" richColors closeButton/>
        </PortalProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
